import React, { useState, useRef, ChangeEvent, useEffect, useMemo } from 'react';
import Dialog from '../Dialog/Dialog';
import { TextButton } from '../Button';
import { importTranslations } from '../../services/translationService';
import languagesData from '../../data/languages.json';
import './ImportTranslationsDialog.css';

interface ImportTranslationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onImportComplete: (stats: {
    addedLanguages: string[];
    addedKeys: number;
    addedStrings: number;
    skippedKeys: number;
    skippedStrings: number;
    errors: string[];
    deletedStrings?: number;
  }) => void;
  isLoading?: boolean;
}

interface LanguageInfo {
  code: string;
  name: string;
}

const ImportTranslationsDialog: React.FC<ImportTranslationsDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  onImportComplete,
  isLoading = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importOptions, setImportOptions] = useState({
    overwriteExisting: false,
    flattenNestedKeys: true,
    resetLanguageList: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState<string | null>(null);
  const [detectedLanguage, setDetectedLanguage] = useState<LanguageInfo | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [parsedData, setParsedData] = useState<Record<string, any> | null>(null);
  const [keyCount, setKeyCount] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasNumericKeys, setHasNumericKeys] = useState<boolean>(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setParsedData(null);
      setSelectedLanguage('');
      setDetectedLanguage(null);
      setKeyCount(0);
      setIsProcessing(false);
      setImportProgress(null);
      setError(null);
      setHasNumericKeys(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  // Update selected language when detected language changes
  useEffect(() => {
    if (detectedLanguage) {
      console.log('Detected language', detectedLanguage);
      setSelectedLanguage(detectedLanguage.code);
    }
  }, [detectedLanguage]);

  // Check if detected language exists in the project
  const isDetectedLanguageNew = useMemo(() => {
    if (!detectedLanguage) return false;
    return !languagesData.languages.some(lang => lang.code === detectedLanguage.code);
  }, [detectedLanguage, languagesData.languages]);

  const detectLanguageFromFilename = (filename: string): LanguageInfo | null => {
    console.log('Attempting to detect language from filename:', filename);
    
    // Extract potential language code from filename
    // Common patterns: en.json, en-US.json, en_US.json, translations.en.json
    const patterns = [
      /[-_]([a-z]{2}(?:[-_][A-Z]{2})?)(?:\.|$)/i, // matches -en, -en-US, -en_US
      /\.([a-z]{2}(?:[-_][A-Z]{2})?)(?:\.|$)/i,   // matches .en, .en-US, .en_US
      /^([a-z]{2}(?:[-_][A-Z]{2})?)(?:\.|$)/i,    // matches en, en-US, en_US at start
    ];

    // Get just the filename without any path
    const fileName = filename.split('/').pop() || filename;
    console.log('Checking filename:', fileName);
    
    // Try to extract language code from the filename
    let langCode: string | null = null;
    for (const pattern of patterns) {
      const match = fileName.match(pattern);
      if (match && match[1]) {
        langCode = match[1].toLowerCase().replace('_', '-');
        console.log('Found language code in filename:', langCode, 'using pattern:', pattern);
        break;
      }
    }

    if (!langCode) {
      console.log('No language code detected from filename');
      return null;
    }

    // Find language in our language data
    const language = languagesData.languages.find(
      lang => lang.code.toLowerCase() === langCode!.toLowerCase() || 
              lang.code.toLowerCase().replace('-', '_') === langCode!.toLowerCase().replace('-', '_')
    );

    if (language) {
      console.log('Matched language code to language:', language.name, language.code);
      return { code: language.code, name: language.name };
    } else {
      console.log('Language code not found in language database:', langCode);
      return null;
    }
  };

  const countKeysInData = (data: Record<string, any>): number => {
    // Count all top-level keys
    let count = 0;
    
    // Iterate through all keys in the data
    for (const key in data) {
      const value = data[key];
      
      // If the value is a string, it's a simple key-value pair
      if (typeof value === 'string') {
        count++;
      } 
      // If the value is an object (like plural forms or nested structures)
      else if (typeof value === 'object' && !Array.isArray(value)) {
        // Check if this looks like a plural form object (has 'one', 'other', etc.)
        const isPluralForm = Object.keys(value).some(k => 
          ['zero', 'one', 'two', 'few', 'many', 'other'].includes(k)
        );
        
        if (isPluralForm) {
          // Count this as a single key with plural forms
          count++;
        } else {
          // For other nested objects, check if they contain language codes
          // or are just nested keys
          const hasLanguageCodes = Object.keys(value).some(k => 
            languagesData.languages.some(lang => 
              lang.code.toLowerCase() === k.toLowerCase() ||
              lang.code.toLowerCase().replace('-', '_') === k.toLowerCase().replace('-', '_')
            )
          );
          
          if (hasLanguageCodes) {
            // This is likely a key with translations for different languages
            count++;
          } else {
            // This is likely a nested structure, count each nested key
            count += Object.keys(value).length;
          }
        }
      }
    }
    
    return count;
  };

  // Helper function to check for numeric keys in the data
  const checkForNumericKeys = (data: Record<string, any>): boolean => {
    let hasNumericKeys = false;
    
    const checkObject = (obj: Record<string, any>) => {
      for (const key in obj) {
        if (/^\d+$/.test(key)) {
          hasNumericKeys = true;
          return;
        }
        
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          checkObject(obj[key]);
        }
      }
    };
    
    checkObject(data);
    return hasNumericKeys;
  };

  // Process file and detect language
  const processFile = async (selectedFile: File) => {
    // Reset state for new file
    setDetectedLanguage(null);
    setSelectedLanguage('');
    setFile(selectedFile);
    setError(null);
    
    try {
      console.log('Processing file:', selectedFile.name);
      
      // Try to detect language from the filename
      const detected = detectLanguageFromFilename(selectedFile.name);
      if (detected) {
        console.log('Detected language from filename:', detected);
      } else {
        console.log('No language detected from filename');
      }
      
      // Read and parse the file
      const fileContent = await selectedFile.text();
      let data: Record<string, any>;
      
      try {
        data = JSON.parse(fileContent);
        setParsedData(data);
        
        // Check for numeric keys
        const hasNumericKeys = checkForNumericKeys(data);
        setHasNumericKeys(hasNumericKeys);
        if (hasNumericKeys) {
          console.warn('Numeric keys detected in the import file. These will be skipped.');
        }
        
        // Count keys
        const count = countKeysInData(data);
        setKeyCount(count);
        
        // Set the detected language from filename if found
        if (detected) {
          setDetectedLanguage(detected);
        }
      } catch (e) {
        setError('Invalid JSON file. Please check the file format.');
        return;
      }
    } catch (e: any) {
      setError(`Error processing file: ${e.message}`);
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await processFile(e.target.files[0]);
    }
  };

  const handleOptionChange = (option: keyof typeof importOptions) => {
    setImportOptions({
      ...importOptions,
      [option]: !importOptions[option],
    });
  };

  const handleLanguageChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedLanguage(e.target.value);
  };

  const handleImport = async () => {
    if (!parsedData || !selectedLanguage) {
      setError('Missing data or language selection');
      return;
    }

    if (!projectId) {
      setError('No project selected');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setImportProgress('0');

    try {
      // Log the data being imported for debugging
      console.log('Importing data:', parsedData);
      console.log('Target language:', selectedLanguage);
      
      // Import the translations with the selected language mapping
      const stats = await importTranslations(projectId, parsedData, {
        overwriteExisting: importOptions.overwriteExisting,
        flattenNestedKeys: importOptions.flattenNestedKeys,
        languageMapping: { '*': selectedLanguage },
        resetLanguageList: importOptions.resetLanguageList,
        onProgress: (current: number, total: number) => {
          // Ensure progress never goes backwards and smoothly transitions
          const percentage = Math.max(2, Math.round((current / total) * 100));
          
          // Only update if the percentage has increased by at least 1%
          setImportProgress(prev => {
            const prevNum = parseInt(prev || '0', 10);
            return percentage > prevNum ? percentage.toString() : prev;
          });
        }
      });
      
      // Ensure we show 100% at the end
      setImportProgress('100');
      console.log('Import stats:', stats);
      
      // Call the onImportComplete callback with the stats
      onImportComplete(stats);
      
      // Close the dialog after a short delay to show 100% completion
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (e: any) {
      console.error('Error during import:', e);
      setError(`Error importing translations: ${e.message}`);
      setImportProgress(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFile(null);
    setDetectedLanguage(null);
    setSelectedLanguage('');
    setParsedData(null);
    setKeyCount(0);
    setHasNumericKeys(false);
    setError(null);
    setImportProgress(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Import"
      className="import-translations-dialog"
    >
      <div className="import-translations-content">
        <div className="file-upload-section">
          <div className="file-upload-container">
            <div 
              className={`file-drop-area ${isDragging ? 'dragging' : ''} ${file ? 'file-selected' : ''} ${isProcessing ? 'processing' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={isProcessing ? undefined : handleFileAreaClick}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept=".json,.csv,.xlsx,.xls"
              />
              {!file ? (
                <>
                  <div className="file-upload-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 16V8M12 8L9 11M12 8L15 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2C16.714 2 19.0711 2 20.5355 3.46447C22 4.92893 22 7.28595 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <p className="file-upload-text">
                    Drag and drop your file here or click to browse
                  </p>
                  <p className="file-upload-hint">
                    Supported formats: JSON, CSV, Excel
                  </p>
                </>
              ) : (
                <div className="selected-file-container">
                  <div className="file-row">
                    
                    <span className="selected-file-name">{file.name}</span>
                    {keyCount > 0 && (
                      <span className="key-count-badge">{keyCount} keys</span>
                    )}
                    <TextButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        resetFileInput();
                      }}
                      size="small"
                      variant="secondary"
                      className="clear-file-button"
                      aria-label="Reset file selection"
                      disabled={isProcessing}
                    >
                      Change
                    </TextButton>
                  </div>
                  
                  <div className="file-divider"></div>
                  
                  <div className="language-row">
                    <select 
                      id="language-select" 
                      value={selectedLanguage} 
                      onChange={handleLanguageChange}
                      className={`language-dropdown ${!selectedLanguage ? 'language-required' : ''}`}
                      onClick={(e) => e.stopPropagation()}
                      required
                      disabled={isProcessing}
                    >
                      <option value="" disabled>Select a language</option>
                      {detectedLanguage && (
                        <option value={detectedLanguage.code}>
                          {isDetectedLanguageNew 
                            ? `Add ${detectedLanguage.name} (${detectedLanguage.code})` 
                            : `${detectedLanguage.name} (${detectedLanguage.code}) - Detected`}
                        </option>
                      )}
                      {languagesData.languages
                        .filter(lang => !detectedLanguage || lang.code !== detectedLanguage.code)
                        .map(lang => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name} ({lang.code})
                          </option>
                        ))
                      }
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {file && !isProcessing && (
          <div className="import-options">
            <label className={`option-label ${isProcessing ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={importOptions.overwriteExisting}
                onChange={() => handleOptionChange('overwriteExisting')}
                disabled={isProcessing}
              />
              <div className="option-text">
                <span className="option-title">Overwrite existing translations</span>
                <span className="option-description">Deletes all existing translations for the selected language</span>
                {importOptions.overwriteExisting && selectedLanguage && (
                  <span className="option-warning">
                    Warning: All existing translations for {
                      languagesData.languages.find(lang => lang.code === selectedLanguage)?.name || selectedLanguage
                    } will be deleted
                  </span>
                )}
              </div>
            </label>
            
            <label className={`option-label ${isProcessing ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={importOptions.flattenNestedKeys}
                onChange={() => handleOptionChange('flattenNestedKeys')}
                disabled={isProcessing}
              />
              <div className="option-text">
                <span className="option-title">Flatten nested keys</span>
                <span className="option-description">e.g., "menu.items.home" → "menu.items.home"</span>
              </div>
            </label>
            
            <label className={`option-label ${isProcessing ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={importOptions.resetLanguageList}
                onChange={() => handleOptionChange('resetLanguageList')}
                disabled={isProcessing}
              />
              <div className="option-text">
                <span className="option-title">Reset language list</span>
                <span className="option-description">Only keep the selected language</span>
                {importOptions.resetLanguageList && (
                  <span className="option-warning">
                    Warning: All other languages will be removed from the project
                  </span>
                )}
              </div>
            </label>
          </div>
        )}

        {hasNumericKeys && !isProcessing && (
          <div className="import-warning">
            <p>
              <strong>Warning:</strong> Numeric keys detected in the import file. 
              These will be skipped to prevent creating numbered translation keys.
            </p>
          </div>
        )}

        {isProcessing && (
          <div className="import-progress-container">
            <div className="progress-status">
              <span className="progress-label">Importing...</span>
              <span className="progress-percentage">{importProgress}%</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar" 
                style={{ width: `${importProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {error && <div className="import-error">{error}</div>}
      </div>

      <div className="dialog-actions">
        <TextButton 
          onClick={onClose} 
          disabled={isProcessing || isLoading}
          size="small"
          variant="secondary"
        >
          Cancel
        </TextButton>
        <TextButton
          onClick={handleImport}
          disabled={!file || !selectedLanguage || isProcessing || isLoading}
          variant="primary"
          size="small"
        >
          {isProcessing ? 'Importing...' : 'Import'}
        </TextButton>
      </div>
    </Dialog>
  );
};

export default ImportTranslationsDialog; 